export default {
  pageTitle: 'Seat layouts',
  pageDesc: 'Maintain reusable global venues, then bind and arrange them when needed.',
  addButton: 'New seating layout',
  notice: 'Seats for onsite events: add sofa, round-table, cinema, or meeting seats; drag to arrange, choose sequential or C-position numbering, and export the finished layout as PNG.',
  filters: 'Seat layout filters',
  searchPlaceholder: 'Search layout name or description',
  allTypes: 'All seat types',
  noMatch: 'No matching seat layouts',
  empty: 'No seat layouts yet',
  clearFilters: 'Clear filters',
  unitCount: 'Units',
  seatCount: 'Total seats',
  editLayout: 'Edit layout',
  countSummary: '{units} units · {seats} seats',
  noDescription: 'No description',
  createdAt: 'Created',
  deleteProtected: 'This venue is linked to an activity or meeting and cannot be deleted.',
  deleteProtectedUsage: 'This venue has usage records and cannot be deleted.',
  deleteProtectedHistory: 'This venue has historical arrangement records and cannot be deleted.',
  createTitle: 'New seating layout',
  editTitle: 'Edit basic information',
  name: 'Layout name',
  namePlaceholder: 'e.g. Opening ceremony main hall',
  description: 'Description',
  descriptionPlaceholder: 'Briefly describe where and how this layout will be used (optional)',
  seatTypes: 'Seat types',
  typeHint: 'Select the seat types needed for this layout. You can add more in the editor.',
  usedTypesHint: 'The seat types used on the canvas are shown above and are read-only. To add more, use Add seat units on the left side of the editor.',
  nameRequired: 'Enter a layout name',
  nameMax: 'Layout name must be 100 characters or fewer',
  descriptionMax: 'Description must be 300 characters or fewer',
  typesRequired: 'Select at least one seat type',
  createAndEdit: 'Create and open editor',
  saveChanges: 'Save changes',
  createSuccess: 'Seat layout created',
  updateSuccess: 'Basic information updated',
  deleteTitle: 'Delete seat layout',
  deleteConfirm: 'Delete “{name}”? This action cannot be undone.',
  deleteHint: 'Venues linked to an activity or meeting cannot be deleted. Venues with only historical usage records can be deleted; the usage records keep the original venue name.',
  deleteSuccess: 'Seat layout deleted',
  deleteFailed: 'Delete failed. Please try again.',
  editor: {
    paletteTitle: 'Add seat units',
    clickToAdd: 'Click to add to canvas',
    typeShort: {
      sofa: 'Sofa',
      round: 'Round',
      cinema: 'Cinema',
      meeting: 'Meeting',
      control: 'Desk'
    },
    capacityTitle: 'Venue capacity',
    capacityUnit: 'people',
    capacityHint: 'Live total of every seat on the canvas',
    capacityEmpty: 'No seats yet. Choose a type above to start.',
    safetyExit: 'Safety exit',
    safetyExitShort: 'EXIT',
    safetyExitHint: 'Drag or click to add',
    deleteSafetyExit: 'Delete safety exit',
    numberRuleTitle: 'Numbering rules',
    numberRuleTitleHint: ' (configurable per unit)',
    numberRuleSequence: 'Sequential: number from row 1, column 1',
    numberRuleCenter: 'C-position: center is 1 and alternates outward (e.g. 10·8·6·4·2·1·3·5·7·9)',
    backToList: 'Back to list',
    backToArrangement: 'Back to seating',
    editInfo: 'Edit basic info',
    a4Hint: 'A4 canvas (export range) · large units can extend beyond the page · numbering and colors are configured per unit',
    unsaved: 'Unsaved changes',
    lastSaved: 'Last saved',
    lastSavedAtValue: 'Last saved: {time}',
    notSaved: 'Not saved yet',
    landscape: 'Landscape',
    portrait: 'Portrait',
    save: 'Save layout',
    skipNumbers: 'Skipped numbers',
    downloadPng: 'Download PNG',
    arrangeSeats: 'Seat arrangement',
    resetCanvas: 'Fit canvas',
    canvasAria: 'SVG seat layout editor canvas',
    stage: 'Stage',
    anchorCount: '{count} anchor pairs',
    scaleLabel: 'Scale 1m={value}px',
    zoomHint: '(Pinch with two fingers on a trackpad to zoom)',
    venueSize: 'Venue size: left {left}m · right {right}m · width {width}m',
    paperLandscape: 'A4 · Landscape (210 × 297mm)',
    paperPortrait: 'A4 · Portrait (210 × 297mm)',
    venueInfo: '{shape} top {top}m / bottom {bottom}m · left {left} / right {right}m{pivotText} (scale 1m={scale}px)',
    pivotSuffix: ' · {count} pivots',
    stageTag: 'STAGE',
    irregularVenue: 'Irregular venue',
    rectVenue: 'Rectangular venue',
    topWidth: 'Top width',
    bottomWidth: 'Bottom width',
    leftLength: 'Left length',
    rightLength: 'Right length',
    venueTitle: 'Venue planning',
    venueDesc: 'Top/bottom widths and side lengths define the venue. Drag the amber pivots to reshape the side walls without rescaling the stage or seats.',
    anchorTitle: 'Venue pivots',
    addPair: 'Add pivot',
    addPivot: '+ Add pivot',
    pairNo: 'Pivot {no}',
    leftX: 'Left X',
    leftY: 'Left Y',
    rightX: 'Right X',
    rightY: 'Right Y',
    removePair: 'Delete pivot',
    selectAnchorHint: 'Select an amber pivot on the canvas to edit it',
    topWidthField: 'Top width (m, h=0)',
    bottomWidthField: 'Bottom width (m, h=height)',
    leftLengthField: 'Left length (m) · anchor',
    rightLengthField: 'Right length (m) · anchor',
    anchorTop: 'Top',
    anchorCenter: 'Middle',
    anchorBottom: 'Bottom',
    pivotTitle: 'Pivot {no} (left / right independent)',
    pivotLeftWidth: 'Left width (m)',
    pivotRightWidth: 'Right width (m)',
    pivotLeftHeight: 'Left height (m from top)',
    pivotRightHeight: 'Right height (m from top)',
    deletePivot: '× Delete',
    pivotEmptyHint: 'Click "Add pivot" to add a control point on the left/right walls.',
    venuePlanNote: 'The A4 page has fixed margins: 120px top, 60px bottom, 60px left/right (red frame). The top and bottom edges are single total widths (symmetric). Each pivot\'s left/right width and left/right height can be adjusted independently: left points form the left wall and right points form the right wall, allowing slanted walls or one-sided bulges. Each pivot\'s left + right widths stay within the top/bottom envelope, and its heights must stay between 0 and the corresponding side length. The left/right side lengths are independent, so the bottom edge can be slanted. The venue is scaled and centered in the red frame by its longest side.',
    seatUnitEditHint: 'Click a seat unit on the canvas',
    seatUnitEditHintSub: 'to edit its name and seat count',
    seatTotal: '{count} seats',
    backToVenue: 'Back to venue planning',
    unitTab: 'Seat unit',
    cardTab: 'Seat card',
    unitName: 'Unit name',
    seatCount: 'Seat count',
    chairWidth: 'Seat width (cm)',
    chairHeight: 'Seat depth (cm)',
    gap: 'Chair gap (cm)',
    diameter: 'Diameter (m)',
    layoutMode: 'Seat arrangement',
    auto: 'Automatic',
    manual: 'Manual',
    autoRoundHint: 'Automatic: fits {count} chairs',
    rows: 'Rows',
    columns: 'Seats per row',
    tableWidth: 'Table length (m)',
    tableHeight: 'Table width (m)',
    meetingTable: 'Meeting table',
    autoMeetingHint: 'Automatic: {long} on long side / {short} on short side',
    sideTop: 'Top seats',
    sideRight: 'Right seats',
    sideBottom: 'Bottom seats',
    sideLeft: 'Left seats',
    color: 'Unit color',
    defaultColor: 'Default (by type)',
    defaultColorShort: 'D',
    assignedSeatColor: 'Assigned seat background',
    assignedColorRandom: 'Random',
    assignedColorUniform: 'Uniform',
    assignedColorHint: 'Uniform by default: assigned seats use the unit color. Random: every assigned seat gets a different color.',
    numberMode: 'Numbering method',
    numberSequence: 'Sequential',
    numberCPosition: 'Center',
    startDirection: 'Start direction (seat 1)',
    cDirection: 'Center direction (head seat)',
    directionTop: 'Top',
    directionRight: 'Right',
    directionBottom: 'Bottom',
    directionLeft: 'Left',
    leftToRight: 'Left to right',
    rightToLeft: 'Right to left',
    oddEvenMode: 'Side sequence (around head seat)',
    leftEvenRightOdd: 'Even left, odd right',
    leftOddRightEven: 'Odd left, even right',
    numberPreview: 'Current seat numbers',
    deleteUnit: 'Delete this unit',
    footerUnits: '{count} seat units',
    footerSeats: '{count} seats',
    minAnchorPairs: 'A venue must keep at least two left/right anchor pairs.',
    deleteUnitConfirm: 'Delete “{name}”? This action cannot be undone.',
    controlSettings: {
      title: 'Control desk settings',
      hint: 'Drag the control desk to reposition it. It does not take up seats or participate in attendee assignment.',
      width: 'Width (cm)',
      depth: 'Depth (cm)',
      delete: 'Delete control desk'
    },
    removeAssignedSeatsTitle: 'Notice',
    removeAssignedSeatsConfirm: 'Changing the layout will remove the seat assigned to {count} person ({names}). This person will return to the unassigned list; other seat assignments will be preserved. Continue? | Changing the layout will remove the seats assigned to {count} people ({names}). They will return to the unassigned list; other seat assignments will be preserved. Continue?',
    reduceSeatsInAssignedAreaConfirm: 'Assigned seats: {names} ({count}), located in {units}. Reducing the seat count may change seat numbering, but these seats will not be removed; other seat assignments will be preserved. Continue?',
    undoStep: 'Undo',
    undoStepHint: 'Undo the last layout change',
    resetToInitial: 'Reset',
    resetToInitialHint: 'Restore the state from when the page was opened or last saved',
    saveSuccess: 'Seat layout saved',
    versionConflict: 'This seat layout was updated elsewhere. Refresh and try again.'
  },
  arrangementDialog: {
    title: 'Seat arrangement · Select attendees',
    intro: 'Select an activity and meeting session, then bind approved registrants or use an imported roster.',
    activity: 'Linked activity',
    activityPlaceholder: 'Select an activity',
    meeting: 'Meeting session',
    meetingPlaceholder: 'Select a meeting session',
    wholeActivity: 'Whole activity (all sessions)',
    rosterSourceLabel: 'Roster source',
    rosterSourceHint: 'Choose the roster source: use approved registrants for this meeting, or download the template and import a roster.',
    rosterSourceRegistration: 'Meeting registrants',
    rosterSourceImported: 'Imported roster',
    rosterCountWhole: 'Approved registrants for the whole activity: {count}',
    rosterCountMeeting: 'Approved registrants for this session: {count}',
    rosterEmptyWhole: 'No approved registrants for this activity yet',
    rosterEmptyMeeting: 'No approved registrants for this session yet',
    rosterEmptyImported: 'No imported roster yet. Download the template, fill it in, and upload it.',
    rosterCountImported: 'Imported attendees: {count}',
    importedTag: 'Imported',
    clearImported: 'Clear imported roster',
    importRequired: 'Upload at least one imported attendee first.',
    rosterNoRegistration: 'This activity has no registration form bound, so no registrants can be read.',
    rosterLoadFailed: 'Failed to load registrants. Please try again.',
    rosterLimit: 'Showing the first {shown} of {total}',
    columns: {
      index: 'No.',
      name: 'Name',
      organization: 'Organization',
      jobTitle: 'Job title',
      status: 'Status'
    },
    approved: 'Approved',
    unnamed: 'Unnamed attendee',
    noActivities: 'No activities available',
    noSeatUnits: 'This layout has no seat units yet. Add seat units before arranging seats.',
    next: 'Next: Arrange seats',
    createSuccess: 'Seat arrangement created. Opening the seat editor…',
    createFailed: 'Failed to create the seat arrangement. Please try again.',
    openFailed: 'The seat arrangement was created, but the editor could not be opened. Open it from the activity seat configuration.'
  },
  skipDialog: {
    title: 'Skip seat numbers',
    introPrefix: 'Choose ',
    introEmphasis: 'numbers to avoid',
    introSuffix: ' for seat units (e.g. 4 and 7). All unit numbering will skip them automatically. For example, 1·2·3·4·5 becomes 1·2·3·5·6 when skipping 4 and 7.',
    commonUses: 'Common uses: Chinese events often avoid 4 and 7; Western events often avoid 13.',
    current: 'Currently skipped',
    removeOne: 'Click to remove this number',
    empty: 'No skipped numbers yet. Once added, all seat unit numbering will avoid them automatically.',
    presets: 'Quick presets',
    chinesePreset: 'Chinese convention (4, 7)',
    chinesePresetTitle: 'Commonly avoided numbers in Hong Kong, Taiwan, and East Asia',
    westernPreset: 'Western convention (13)',
    westernPresetTitle: 'Some Western events and floors avoid 13',
    custom: 'Add custom numbers',
    placeholder: 'e.g. 4 or 4,7,9-12 (comma-separated, ranges supported)',
    add: 'Add',
    helpPrefix: 'Supports single numbers (e.g. ',
    helpSingle: '4',
    helpListPrefix: '), lists (e.g. ',
    helpList: '4,7,9',
    helpRangePrefix: '), or ranges (e.g. ',
    helpRange: '9-12',
    helpSuffix: ' means 9, 10, 11, and 12).',
    clear: 'Clear all',
    apply: 'Apply to canvas',
    applied: 'Skipped numbers applied and seat numbers regenerated'
  },
  card: {
    enable: 'Enable seat cards',
    enableHint: 'All seats in this unit share the same card style',
    size: 'Finished size',
    sizeHints: {
      '90x55': 'Main folded small card, compact and portable',
      '105x74': 'Main folded large card, more room for information'
    },
    background: 'Background image',
    clickUpload: 'Click to upload',
    uploading: 'Uploading…',
    uploadHint: 'JPG / PNG only. Match the finished card aspect ratio.',
    removeImage: 'Remove background image',
    replaceImage: 'Replace',
    fitMode: 'Image fit',
    cover: 'Cover',
    contain: 'Contain',
    printFields: 'Printed information',
    fieldName: 'Name',
    fieldOrganization: 'Organization',
    fieldJobTitle: 'Job title',
    nameSize: 'Name size (pt)',
    textSize: 'Other text size (pt)',
    textColor: 'Text color',
    preview: 'Preview',
    sampleName: 'Sample name',
    sampleJobTitle: 'Sample job title',
    sampleOrganization: 'Sample organization',
    note: 'One seat card is printed per seat with only the selected name, organization, and job title. The image fills the card front according to the selected fit mode; a white background is used when no image is set.',
    notePrint: 'Seat cards are used for printing or placing on seats.',
    noteImage: 'The image follows the selected fit mode. A white card is used without an image.',
    noteSample: 'Preview text is for reference only; actual printing uses assigned participants.',
    uploadFailed: 'Image upload failed. Please try again.',
    removeFailed: 'Failed to remove the background image'
  },
  png: {
    title: 'Download PNG',
    scale: 'Resolution',
    transparent: 'Transparent background',
    transparentHint: 'Do not export the white A4 background',
    filename: 'File name',
    export: 'Download',
    success: 'PNG generated',
    failed: 'PNG export failed. Please try again.'
  },
  types: {
    sofa: 'Sofa',
    round: 'Round table',
    cinema: 'Cinema',
    meeting: 'Meeting',
    control: 'Control desk'
  },
  typeHints: {
    sofa: 'Single row for stages and head tables',
    round: 'Seats around round tables, ideal for banquets',
    cinema: 'Cinema-style grid for forums and training',
    meeting: 'Hollow tables auto-arranged around chairs for discussions or banquets'
  }
}
